import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DrawnSignatureSource,
	EidEasySigningMethod,
} from '@dealstack/shared/lib/common/eideasy/signingMethods';
import { BulkProgress, EidEasySignStatus } from './EidEasySigningModal.types';
import type { OtpResendOutcome } from './EidEasySigningModal.types';
import { DocumentReviewStep } from './steps/DocumentReviewStep';
import { MethodSelectStep } from './steps/MethodSelectStep';
import { SmartIdInputStep } from './steps/SmartIdInputStep';
import { SignDrawStep } from './steps/SignDrawStep';
import { FrejaInputStep } from './steps/FrejaInputStep';
import { ProgressStep } from './steps/ProgressStep';
import { AwaitingSignStep } from './steps/AwaitingSignStep';
import { DoneStep } from './steps/DoneStep';
import type { SignatureSuccessDetails } from './steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation.types';
import { ErrorStep } from './steps/ErrorStep';
import { OtpStep } from './steps/OtpStep';
import { OtpCodeEntryStep } from './steps/OtpCodeEntryStep';
import { SetupSignatureStep } from './steps/SetupSignatureStep';
import { SignerContactType } from '@dealstack/shared/lib/generated/models/SignerContactType';

interface Props {
	status: EidEasySignStatus;
	challengeCode: string | null;
	error: string | null;
	/** The signing method active when `error` was set; shown on the error step. */
	selectedMethod?: EidEasySigningMethod | null;
	allowedMethods: EidEasySigningMethod[];
	restrictToCountry?: boolean;
	countryCode?: string | null;
	onClose: () => void;
	onConfirmReview?: () => void;
	onSelectMethod: (method: EidEasySigningMethod) => void;
	onGoBack: () => void;
	onConfirmSmartId?: (idcode: string, countryCode: string) => void;
	onConfirmFreja?: (email: string) => void;
	onConfirmSignature?: (signatureDataUrl: string, source: DrawnSignatureSource) => void;
	onSignatureConfirmed?: (
		signatureBase64: string,
		source: DrawnSignatureSource
	) => void | Promise<void>;
	onConfirmOtp?: (channel: SignerContactType, value: string) => void;
	onSubmitOtpCode?: (code: string) => void;
	onResendOtpCode?: () => Promise<OtpResendOutcome>;
	otpVerifying?: boolean;
	otpCodeError?: string | null;
	otpChannel?: SignerContactType;
	otpMaskedContact?: string;
	defaultPhone?: string;
	defaultPhonePrefix?: string;
	defaultEmail?: string;
	progress?: BulkProgress | null;
	disableSavedSignature?: boolean;
	hideStepHeader?: boolean;
	successDetails?: SignatureSuccessDetails;
}

const noop = () => undefined;
const noopResend = (): Promise<OtpResendOutcome> => Promise.resolve({ status: 'failed' });

export const SigningPanel = ({
	status,
	challengeCode,
	error,
	selectedMethod,
	allowedMethods,
	restrictToCountry,
	countryCode,
	onClose,
	onConfirmReview,
	onSelectMethod,
	onGoBack,
	onConfirmSmartId,
	onConfirmSignature,
	onSignatureConfirmed,
	onConfirmFreja,
	onConfirmOtp,
	onSubmitOtpCode,
	onResendOtpCode,
	otpVerifying,
	otpCodeError,
	otpChannel,
	otpMaskedContact,
	defaultPhone,
	defaultPhonePrefix,
	defaultEmail,
	progress,
	disableSavedSignature,
	hideStepHeader,
	successDetails,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	switch (status) {
		case EidEasySignStatus.DOCUMENT_REVIEW:
			return <DocumentReviewStep onConfirm={onConfirmReview ?? noop} onCancel={onClose} />;
		case EidEasySignStatus.METHOD_SELECT:
			return (
				<MethodSelectStep
					methods={allowedMethods}
					onSelectMethod={onSelectMethod}
					restrictToCountry={restrictToCountry}
					countryCode={countryCode}
				/>
			);
		case EidEasySignStatus.SETUP_SIGNATURE:
			return (
				<SetupSignatureStep
					hideStepHeader={hideStepHeader}
					onConfirm={onSignatureConfirmed ?? noop}
					onCancel={onGoBack}
					disableSavedSignature={disableSavedSignature}
				/>
			);
		case EidEasySignStatus.SMART_ID_INPUT:
			return (
				<SmartIdInputStep
					onConfirm={onConfirmSmartId ?? noop}
					onGoBack={onGoBack}
					hideStepHeader={hideStepHeader}
				/>
			);
		case EidEasySignStatus.FREJA_INPUT:
			return (
				<FrejaInputStep
					onConfirm={onConfirmFreja ?? noop}
					onGoBack={onGoBack}
					defaultEmail={defaultEmail}
					hideStepHeader={hideStepHeader}
				/>
			);
		case EidEasySignStatus.DRAW_SIGNATURE:
			return (
				<SignDrawStep
					onConfirmSignature={onConfirmSignature ?? (() => undefined)}
					onGoBack={onGoBack}
					disableSavedSignature={disableSavedSignature}
					hideStepHeader={hideStepHeader}
				/>
			);
		case EidEasySignStatus.OTP_INPUT:
			return (
				<OtpStep
					onConfirm={onConfirmOtp ?? noop}
					onGoBack={onGoBack}
					defaultPhone={defaultPhone}
					defaultPhonePrefix={defaultPhonePrefix}
					defaultEmail={defaultEmail}
					hideStepHeader={hideStepHeader}
				/>
			);
		case EidEasySignStatus.OTP_CODE_ENTRY:
			if (!otpChannel || !otpMaskedContact) {
				return null;
			}
			return (
				<OtpCodeEntryStep
					channel={otpChannel}
					maskedContact={otpMaskedContact}
					onSubmit={onSubmitOtpCode ?? noop}
					onResend={onResendOtpCode ?? noopResend}
					verifying={otpVerifying ?? false}
					codeError={otpCodeError ?? null}
					onGoBack={onGoBack}
					hideStepHeader={hideStepHeader}
				/>
			);
		case EidEasySignStatus.PREPARING:
			return (
				<ProgressStep label={tCommonUi('eid_easy_signing.preparing')} progress={progress} />
			);
		case EidEasySignStatus.AWAITING_SIGN:
			return <AwaitingSignStep challengeCode={challengeCode} />;
		case EidEasySignStatus.COMPLETING:
			return (
				<ProgressStep
					label={tCommonUi('eid_easy_signing.finalising')}
					progress={progress}
				/>
			);
		case EidEasySignStatus.DONE:
			return <DoneStep successDetails={successDetails} />;
		case EidEasySignStatus.ERROR:
			return (
				<ErrorStep error={error} selectedMethod={selectedMethod} onTryAgain={onGoBack} />
			);
		case EidEasySignStatus.IDLE:
		default:
			return null;
	}
};
